import React, { useState, useEffect, useRef } from 'react';
import {
    Row, Col, Alert, Button,
    Modal, ModalHeader, ModalBody, ModalFooter,
    Progress
} from 'reactstrap';
import withApolloClient from '../../../withApolloClient';
import { getMvrReportMutation } from './Mutations';
import { DriverDetailQuery } from './Queries';
import { DriverDocumentsQuery } from '../../../Material/DocumentModal/Quries';

const GenerateMVR = ({ driver, open, handleClose, ...props }) => {
    const [error, setError] = useState("");
    const [warning, setWarning] = useState("");
    const [success, setSuccess] = useState("");
    const [loading, setLoading] = useState(false);
    const [mvrStartTime, setMvrStartTime] = useState(null);
    const intervalRef = useRef(null);

    const isSubmitDisabled = loading || success ||
        !driver || !driver?.dmvLicense || !driver?.state || !driver?.dob;

    // Setup polling every 5 seconds while loading
    useEffect(() => {
        if (loading && mvrStartTime) {
            intervalRef.current = setInterval(() => {
                const now = new Date();
                const elapsed = Math.floor((now - mvrStartTime) / 1000);
                setWarning(`MVR is still being generated (${elapsed}s elapsed)... please wait.`);
            }, 5000);
        }

        return () => {
            clearInterval(intervalRef.current);
        };
    }, [loading, mvrStartTime]);

    const generateMvr = (e) => {
        e.preventDefault();
        try {
            const { apolloClient } = props;
            if (!apolloClient) {
                throw new Error("GQL Client not found");
            }
            if (!driver?.dmvLicense || !driver?.state || !driver?.dob) {
                setError("Driver information is incomplete. Please ensure DMV License, State, and Date of Birth are provided.");
                return;
            }

            setError("");
            setSuccess("");
            setWarning("");
            setMvrStartTime(new Date());
            setLoading(true);

            const input = { driverId: driver.id };
            apolloClient.mutate({
                mutation: getMvrReportMutation,
                refetchQueries: [DriverDetailQuery, DriverDocumentsQuery],
                variables: { input },
            }).then((result) => {
                setLoading(false);
                clearInterval(intervalRef.current);
                setWarning(""); // Clear warning when operation is done
                if (!result || !result.data?.getMvrReport) {
                    throw new Error("");
                }

                if (result?.data?.getMvrReport?.ok && result?.data?.getMvrReport?.reportData) {
                    // Show success and clear in 5 seconds
                    setSuccess("MVR generated successfully! See driver documents for result. Closing modal in 5 seconds...");
                    setTimeout(() => {
                        setSuccess("");
                        handleClose();
                    }, 5000);
                } else {
                    const messages = result.data.getMvrReport.errors?.[0]?.messages?.[0] || "Error generating MVR";
                    throw new Error(messages);
                }
            }).catch((error) => {
                setError("Failed: " + error.toString());
                setLoading(false);
                clearInterval(intervalRef.current);
            });
        } catch (error) {
            setError("Failed: " + error.toString());
            setLoading(false);
            clearInterval(intervalRef.current);
        }  
    };

    return (
        <Modal className="Add-driver-modal" isOpen={open}>
            <ModalHeader>Generate MVR For Driver</ModalHeader>
            <ModalBody>
                <Row>
                    {loading && (<Col xs={12}><Progress  style={{marginBottom: 10}} animated color="info" value="100" /></Col>)}
                    {error && (<Col xs={12}><Alert color="danger">
                    {error.split("\n").map((line, index) => (
                        <div key={index}>{line}</div>
                    ))}    
                    </Alert></Col>)}
                    {success && (<Col xs={12}><Alert color="success">{success}</Alert></Col>)}
                    {warning && (<Col xs={12}><Alert color="warning">{warning}</Alert></Col>)}
                </Row>
                <Row>
                    <Col>
                        <Alert color="warning">
                            You're about to generate an MVR for a driver. Please ensure that the driver's information is correct before proceeding.
                        </Alert>
                    </Col>
                </Row>
                <Row className="mb-3">
                    <Col>
                        <div style={{
                            border: '1px solid #ddd',
                            borderRadius: '8px',
                            padding: '16px',
                            backgroundColor: '#f9f9f9'
                        }}>
                            <h6 className="mb-3"><strong>Driver Information</strong></h6>
                            <p style={{ color: !driver?.name ? 'red' : 'inherit' }}><strong>Name:</strong> {driver?.name || '--'}</p>
                            <p style={{ color: !driver?.dmvLicense ? 'red' : 'inherit' }}><strong>DMV License:</strong> {driver?.dmvLicense || '--'}</p>
                            <p style={{ color: !driver?.state ? 'red' : 'inherit' }}><strong>State:</strong> {driver?.state || '--'}</p>
                            <p style={{ color: !driver?.dob ? 'red' : 'inherit' }}><strong>Date of Birth:</strong> {driver?.dob || '--'}</p>
                        </div>
                    </Col>
                </Row>
            </ModalBody>
            <ModalFooter>
                <Button outline className="Add-driver-modal-secondary-btn" onClick={handleClose}>
                    {success ? "Close" : "Cancel"}
                </Button>
                <Button
                    className="Add-driver-modal-primary-btn"
                    onClick={generateMvr}
                    disabled={isSubmitDisabled}
                >
                    {loading ? "Loading..." : "Generate MVR"}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default withApolloClient(GenerateMVR);
